import {
  Avatar,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import WSBackButton from "../../../Components/BackButton/BackButton";
import WSDataGridNew, { IColumns } from "../../../Components/DataGrid/WSDataGridNew";
import { useNavigate, useParams } from "react-router-dom";
import { IHeaderRows } from "../../../Components/Filter/WSHeaderFilter";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import http from "../../../commonFunctions/httpClient";
import {
  getLocalValue,
  LoacalVariables,
} from "../../../commonFunctions/CommonFunctions";

// API Response interfaces (based on your sample data)
interface Patient {
  customerProfileId: string;
  name: string;
}

interface Address {
  line1: string;
  line2: string;
  pinCode: string;
}

interface Service {
  testId: string;
  testName: string | null;
  rate: number;
  reportUrl: string;
}

interface LabOrderResponse {
  bookingId: string;
  labId: string;
  labName: string;
  testId: string | null;
  testName: string | null;
  userId: string;
  username: string;
  patients: Patient[];
  slotId: string;
  slotTime: string;
  scheduledDate: string;
  collectionType: string;
  address: Address;
  labContact: string | null;
  paymentMode: string;
  paymentStatus: string;
  rate: number;
  collectionFee: number;
  serviceFee: number;
  totalAmount: number;
  status: string;
  trackingTimeline: any[];
  services: Service[];
  message: string;
}

interface AssignLabOrderRow {
  backendBookingId: string;
  id: string;
  orderId: string;
  date: string;
  patientName: string;
  testPackages: string;
  address: string;
  status: string;
  actions: string;
}

interface LabProfile {
  labName: string;
  id: string;
  experience: string;
  profileImage?: string;
  status: string;
}

interface AssignTechnicianPayload {
  technicianId: string;
  labBookingId: string;
}

export const LabAssignOrder: React.FC<any> = React.memo(() => {
  const params = useParams();
  const id = params?.id; // technician ID from URL
  const navigate = useNavigate();

  const [labId, setLabId] = useState<string>("");
  const [orders, setOrders] = useState<AssignLabOrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiLoading, setApiLoading] = useState<Set<string>>(new Set());
  const [labProfile, setLabProfile] = useState<LabProfile | null>(null);
  const [labLoading, setLabLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info"; // ✅ FIXED: Added "info"
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // vendor ID
  useEffect(() => {
    const userId = getLocalValue(LoacalVariables.UserId);
    setLabId(userId || "");
  }, []);

  // map API -> grid rows
  const mapOrdersFromResponse = (responseData: LabOrderResponse[]): AssignLabOrderRow[] => {
    return responseData.map((order: LabOrderResponse) => {
      const formatAddress = (address: Address) => {
        if (!address) return "N/A";
        const parts: string[] = [];
        if (address.line1) parts.push(address.line1);
        if (address.line2) parts.push(address.line2);
        if (address.pinCode) parts.push(address.pinCode);
        return parts.join(", ") || "N/A";
      };

      const backendBookingId: string = order.bookingId;

      return {
        backendBookingId,
        id: backendBookingId,
        orderId: order.bookingId,
        date: order.scheduledDate.split('T')[0],
        patientName: order.patients.map(p => p.name).join(', '),
        testPackages: order.services.map(s => s.testName || `Test (${s.rate})`).join(', ') || 'N/A',
        status: order.status || "Pending",
        address: formatAddress(order.address),
        actions: backendBookingId,
      };
    });
  };

  const fetchOrders = useCallback(async (showLoading = true) => {
    if (!labId || !id) {
      if (showLoading) setLoading(false);
      return;
    }

    try {
      if (showLoading) setLoading(true);

      const response: any = await http.get(
        `lab/LabBooking/lab/${labId}`
      );

      if (response?.success && Array.isArray(response.data)) {
        const mapped = mapOrdersFromResponse(response.data);
        setOrders(mapped);
      } else {
        setOrders([]);
      }
    } catch (error: any) {
      console.error("Fetch lab orders error:", error);
      setOrders([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [labId, id]);

  // ✅ FIXED: Moved fetchOrders to top and proper dependency order
  const handleAssignToggle = useCallback(async (backendBookingId: string) => {
    console.log("handleAssignToggle called with:", backendBookingId);

    if (!id) {
      setSnackbar({
        open: true,
        message: "Missing technician ID",
        severity: "error",
      });
      return;
    }

    const currentOrder = orders.find(o => o.backendBookingId === backendBookingId);
    if (!currentOrder) {
      setSnackbar({
        open: true,
        message: "Order not found",
        severity: "error",
      });
      return;
    }

    // Check if already assigned
    if (currentOrder.status === "Assigned") {
      setSnackbar({
        open: true,
        message: "Order already assigned",
        severity: "info", // ✅ FIXED: Changed from "warning" to "info"
      });
      return;
    }

    setApiLoading((prev) => {
      const s = new Set(prev);
      s.add(backendBookingId);
      return s;
    });

    try {
      const payload: AssignTechnicianPayload = {
        technicianId: id,
        labBookingId: backendBookingId
      };

      console.log("Assign payload:", payload);

      const response: any = await http.post("lab/CollectionDetailes", payload);

      if (response?.success === false) {
        throw new Error(response?.message || "API failed");
      }

      await fetchOrders(false);

      setSnackbar({
        open: true,
        message: "Order assigned to technician successfully ✅",
        severity: "success",
      });
    } catch (error: any) {
      console.error("Assign Error:", error);
      const errorMsg = error?.response?.data?.message || "Failed to assign order";
      setSnackbar({ 
        open: true, 
        message: errorMsg, 
        severity: "error" 
      });
    } finally {
      setApiLoading((prev) => {
        const s = new Set(prev);
        s.delete(backendBookingId);
        return s;
      });
    }
  }, [id, orders, fetchOrders]); // ✅ FIXED: Now works properly

  useEffect(() => {
    const fetchLabProfile = async () => {
      if (!id || !labId) {
        setLabLoading(false);
        return;
      }

      try {
        setLabLoading(true);
        const response: any = await http.get(`/lab/${labId}/labs`);
        
        if (response?.success && Array.isArray(response.data)) {
          const lab = response.data.find((l: any) => l.labId === id);
          if (lab) {
            setLabProfile({
              labName: lab.labName ||  "Unknown Technician",
              id: lab.labId || id,
              experience: `${lab.experience || 3} yrs`,
              profileImage: lab.profileImage 
                ? `https://your-domain.com${lab.profileImage}` 
                : undefined,
              status: lab.status || "Active",
            });
          }
        }
      } catch (error) {
        console.error("Lab profile error:", error);
      } finally {
        setLabLoading(false);
      }
    };

    fetchLabProfile();
  }, [id, labId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    breadCrumbsStore.updateBreadCrumbs([
      { "Lab Management": "lab-technician-manage/list" },
      { "Assign Order": `/lab-assign/${id}` },
    ]);
  }, [id]);

  const columns: IColumns[] = [
    { header: "Order ID", fieldName: "orderId", type: "default" },
    { header: "Date", fieldName: "date", type: "date" },
    { header: "Patient Name", fieldName: "patientName", type: "default" },
    { header: "Test/Packages", fieldName: "testPackages", type: "default" },
    { header: "Address", fieldName: "address", type: "default" },
    { header: "Status", fieldName: "status", type: "status" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: ["Assign"],
      actionCallback: (action: string, row: AssignLabOrderRow) => {
        if (action === "Assign") {
          handleAssignToggle(row.backendBookingId);
        }
      },
    },
  ];

  const filterRows: IHeaderRows[] = [
    {
      label: "By Date",
      fieldName: "date",
      type: "date",
      operator: "gte",
      rowDivision: 6,
    },
    {
      label: "Status",
      fieldName: "status",
      type: "dropdown",
      options: [
        { label: "All", value: "" },
        { label: "OrderPlaced", value: "OrderPlaced" },
        { label: "Assigned", value: "Assigned" },
        { label: "Pending", value: "Pending" },
      ],
      operator: "eq",
      rowDivision: 3,
    },
  ];

  const handleSnackbarClose = () => {
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  if (loading) {
    return (
      <Grid container spacing={2} mt={1} sx={{ paddingLeft: 1 }}>
        <Grid
          item
          sm={12}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <div>Loading lab orders...</div>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <WSBackButton id="btnScheduleBack" url={"/lab-technician-manage/list"} />

      {/* Lab Profile */}
      <Paper
        sx={{
          mb: 3,
          mt: 2,
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Grid>
          <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A1A", mb: 0.5 }}>
            Technician Profile
          </Typography>
          <Divider />
        </Grid>
        <Grid sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Grid sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              src={labLoading ? undefined : labProfile?.profileImage}
              sx={{
                width: 56,
                height: 56,
                bgcolor: labLoading ? "grey.300" : "primary.main",
              }}
            >
              {labLoading ? (
                <CircularProgress size={24} color="inherit" thickness={5} />
              ) : (
                labProfile?.labName?.charAt(0)?.toUpperCase() || " "
              )}
            </Avatar>
            <Grid>
              <Typography fontWeight={600}>
                {labLoading ? "Loading..." : labProfile?.labName || ""}
              </Typography>
              <Typography variant="body2" color="gray">
                ID: {labLoading ? "Loading..." : labProfile?.id || id}
              </Typography>
              <Typography variant="body2" color="gray">
                {labLoading ? "Loading..." : labProfile?.experience}
              </Typography>
            </Grid>
          </Grid>
          <Chip
            label={labLoading ? "Loading..." : labProfile?.status || "Active"}
            color={
              labLoading
                ? "default"
                : labProfile?.status === "Active"
                ? "success"
                : "default"
            }
            size="small"
          />
        </Grid>
      </Paper>

      {/* Orders Grid */}
      <Paper
        elevation={0}
        style={{
          paddingLeft: 20,
          paddingRight: 20,
          marginTop: 20,
          paddingBottom: 25,
          borderRadius: "8px",
        }}
      >
        <Grid container spacing={2}>
          <Grid item sm={12} xs={12}>
            <WSDataGridNew
              columns={columns}
              id="labAssignOrderListGrid"
              title={`Assign Orders - Technician ${id?.substring(0, 8)}... (${orders.length})`}
              exportCsv={true}
              filterFields={filterRows}
              data={orders}
              searchBox={{ fieldName: "patientName" }}
              gridHeaderSx={{
                backgroundColor: "whitesmoke",
                border: "1px solid whitesmoke",
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
});

export default LabAssignOrder;
